import { createContext, useCallback, useContext, useRef, type ReactNode } from 'react';
import { useFotbollPlayApi } from './useFotbollPlayApi';
import { FotbollPlayTabIndex, FotbollPlayTabPayloadMap } from './types';

interface FotbollPlaySubmitContextValue {
  isSubmitting: boolean;
  registerTabPayload: <T extends FotbollPlayTabIndex>(
    tab: T,
    getPayload: () => FotbollPlayTabPayloadMap[T]
  ) => () => void;
  submitActiveTab: (activeTab: FotbollPlayTabIndex) => Promise<void>;
}

const FotbollPlaySubmitContext = createContext<FotbollPlaySubmitContextValue | null>(null);

interface FotbollPlaySubmitProviderProps {
  teamAbbrv: string;
  children: ReactNode;
}

export function FotbollPlaySubmitProvider({ teamAbbrv, children }: FotbollPlaySubmitProviderProps) {
  const { isSubmitting, submit } = useFotbollPlayApi(teamAbbrv);
  const payloadGettersRef = useRef<
    Partial<Record<FotbollPlayTabIndex, () => FotbollPlayTabPayloadMap[FotbollPlayTabIndex]>>
  >({});

  const registerTabPayload = useCallback(
    <T extends FotbollPlayTabIndex>(tab: T, getPayload: () => FotbollPlayTabPayloadMap[T]) => {
      payloadGettersRef.current[tab] =
        getPayload as () => FotbollPlayTabPayloadMap[FotbollPlayTabIndex];

      return () => {
        if (payloadGettersRef.current[tab] === getPayload) {
          delete payloadGettersRef.current[tab];
        }
      };
    },
    []
  );

  const submitActiveTab = useCallback(
    async (activeTab: FotbollPlayTabIndex) => {
      const getPayload = payloadGettersRef.current[activeTab];
      if (!getPayload) {
        return;
      }

      await submit(activeTab, getPayload() as FotbollPlayTabPayloadMap[typeof activeTab]);
    },
    [submit]
  );

  return (
    <FotbollPlaySubmitContext.Provider
      value={{ isSubmitting, registerTabPayload, submitActiveTab }}
    >
      {children}
    </FotbollPlaySubmitContext.Provider>
  );
}

export function useFotbollPlaySubmit(): FotbollPlaySubmitContextValue {
  const context = useContext(FotbollPlaySubmitContext);
  if (!context) {
    throw new Error('useFotbollPlaySubmit must be used within FotbollPlaySubmitProvider');
  }
  return context;
}
